/**
 * The product catalog: product list and editor, variants, the shop's
 * option types, stock editing, tags and the product picker. `shared` and
 * `seo` also serve the category editor (categories namespace), which has
 * the same fields and SEO box.
 */
export default {
    shared: {
        sku: 'SKU',
        stock: 'Stock',
        variants: 'Variants',
        variant: 'Variant',
        featured: 'Featured',
        categories: 'Categories',
        details: 'Details',
        visibility: 'Visibility',
        general: 'General',
        priceEx: 'Price (ex VAT)',
        priceIncl: 'Price (incl. VAT)',
        slugHint: 'URL segment; auto-derived from name if blank',
        localeSlugHint: 'URL segment for {locale}; blank keeps /{slug}',
        localeFallback: 'Leave a field blank to fall back to the default-locale text.',
        localeDescriptionFallback: 'Blank = fall back to the default-locale description.',
        replaceImage: 'Replace image',
        noImagePath: '(no image)',
        pickImage: 'Pick or upload an image',
        addValueTo: 'Add a value to {option}',
        noCategories: 'No categories yet.',
    },
    seo: {
        tab: 'SEO',
        searchEngines: 'Search engines',
        pageTitle: 'Page title',
        pageTitleHint: 'Shown as the browser tab and the search result headline',
        metaDescription: 'Meta description',
        metaDescriptionHint: 'Optimal length is 120–158 characters',
        socialTitle: 'Social title',
        socialTitleHint: 'Open Graph title used when the page is shared',
        socialDescription: 'Social description',
        advanced: 'Advanced',
        sharedByLanguages: 'Shared by every language.',
        socialImage: 'Social image',
        canonicalOverride: 'Canonical override',
        robots: 'Robots',
        robotsDefault: 'Blank = the website default (index,follow).',
        intro: 'Leave a field blank to use the default: the product name for the title, the short description for the meta description.',
        localeFallback: "Blank = use the {locale} value, then the product's own name in this language.",
        socialImageHint: "Blank = the product's first image",
        robotsSiteNoindex: 'The website is hidden from search engines, so this product sends noindex,nofollow whatever is set here. Change it in Website → General → Search engines.',
    },
    list: {
        title: 'Products',
        importCsv: 'Import CSV',
        newProduct: 'New product',
        searchPlaceholder: 'Search by name or SKU…',
        allCategories: 'All categories',
        anyStock: 'Any stock',
        outOfStock: 'Out of stock',
        empty: 'No products match these filters.',
        priceEx: '{price} ex VAT',
        priceIncl: '{price} incl. VAT',
        variantCount: '{n} variant | {n} variants',
        stockOf: 'Stock of {name}',
        stockOfVariant: 'Stock of {name}, {variant}',
        live: 'Live',
        hidden: 'Hidden',
        total: 'no products | {n} product | {n} products',
        perPage: '{select} per page',
        pageOf: 'Page {page} of {total}',
    },
    edit: {
        newProduct: 'New product',
        created: 'Product created',
        confirmDelete: 'Delete product "{name}"? This cannot be undone.',
        tabs: {
            pricing: 'Pricing',
            stock: 'Stock & ordering',
            images: 'Images',
            organisation: 'Organisation',
        },
        skuHint: 'Unique within this website',
        brand: 'Brand',
        brandHint: "Blank uses the shop's default brand",
        gtin: 'Barcode (EAN / GTIN)',
        gtinHint: '8, 12, 13 or 14 digits, as under the bars',
        shortDescription: 'Short description',
        access: 'Access',
        imagesIntro: 'First image is the cover. Click an image to replace it; use the arrows to reorder.',
        altText: 'Alt text',
        altPlaceholder: 'Describe the image',
        addImage: '+ Add image',
        pricing: 'Pricing',
        priceEntry: 'Price entry',
        exVat: 'Ex VAT',
        inclVat: 'Incl. VAT',
        currency: 'Currency',
        priceSummaryGross: 'Ex VAT: {net} ({rate}% VAT: {tax})',
        priceSummaryNet: 'Incl. {rate}% VAT: {gross}',
        taxClass: 'Tax class',
        grossKeepsNote: 'The price incl. VAT stays as typed when the rate changes; ex VAT moves.',
        vatCategory: 'VAT category',
        followsTaxClass: 'Follows the tax class',
        checkedFor: 'Checked for:',
        inventory: 'Inventory',
        giftCardNoStock: 'Stock is not counted for gift cards: they are made on demand, so a count could only ever be wrong.',
        stockPerVariant: 'Stock is counted per variant.',
        editOnVariantsTab: 'Edit on the Variants tab',
        trackStock: 'Track stock',
        notTracked: 'Not counted: the shop never runs out of this product.',
        stockFromList: 'Stock can also be updated straight from the product list.',
        ordering: 'Ordering',
        minOrderQuantity: 'Minimum order quantity',
        minOrderQuantityHint: '1 = no minimum. The cart refuses smaller quantities (e.g. a buffet from 15 people).',
        weight: 'Weight (g)',
        weightHint: 'Used by shipping module',
        customerNote: 'Customer note',
        customerNoteSwitch: 'Ask the customer for a note',
        customerNoteIntro: "Adds a text box to this product's add-to-cart form. What the customer types travels with the cart line onto the order, the order confirmation and the admin order detail.",
        customerNoteLabel: 'Question above the box',
        customerNotePlaceholder: 'Any allergies or wishes?',
        customerNoteHint: 'Translate it per language with the language tabs above. Two of the same product with different notes stay separate cart lines.',
        giftCard: 'Gift card',
        giftCardSwitch: 'This product is a gift card',
        giftCardIntro: 'Buying it mints a cadeaubon the customer can spend at checkout, instead of shipping something. Give it one variant per amount you want to offer (25, 50, 100) and the shopper picks one. The add-to-cart form then also asks who it is for, with an optional message and send date.',
        giftCardModule: 'The {module} module must be on for this website, or nothing is minted when the order is paid.',
        giftCardModuleName: 'Gift cards',
        tags: 'Tags',
        tagsIntro: 'Tags help shoppers discover products. Type a name and press Enter to create a new tag.',
        variantMode: 'Variant mode',
        variantModeSwitch: 'This product sells via variants',
        variantModeIntro: "When on, the storefront shows a variant picker and stock/price come from individual variants. Toggling off keeps the variant rows but the storefront uses the product's own price/stock again.",
    },
    variants: {
        intro: 'Define option types (Size, Color, …) and their values, then generate a variant for every combination. Each variant has its own SKU, barcode, price, and stock. The storefront shows a picker on the product page.',
        optionsHeading: 'Options this product varies by',
        detachOption: 'Stop using this option on this product',
        deleteValueEverywhere: 'Delete "{value}" from {option} everywhere in this shop',
        noOptions: 'None yet. Add one (Size for a shirt, Amount for a gift card), then give it the values you want to offer.',
        addOptionType: 'Add option type',
        alreadyInShop: 'Already in this shop',
        newOptionPlaceholder: 'Or a new one (e.g. Amount)',
        generate: 'Generate',
        addManually: 'Add manually',
        empty: 'No variants yet. Add an option above with the values you want to offer, then click Generate.',
        generateTitle: 'Generate variants',
        generateIntro: "Tick the option values to include. We'll create a variant for every combination ({n} total).",
        defaultPrice: 'Default price',
        defaultStock: 'Default stock',
        generateCount: 'Generate {n}',
        order: 'Order',
        dragHint: 'Drag to change the order',
        detachFailed: 'Could not remove that option.',
        confirmDeleteValue: 'Delete this value? Variants using it lose the tag.',
        pickValue: 'Pick at least one option value.',
        generated: 'Generated {n} variant ({skipped} already existed). | Generated {n} variants ({skipped} already existed).',
        checkValues: 'Please check the option values.',
        saved: 'Saved {name}',
        confirmDelete: 'Delete variant {name}?',
        gtin: 'Barcode',
        gtinPlaceholder: 'EAN / GTIN',
    },
    options: {
        title: 'Product options',
        intro: 'The dimensions products can vary by (Size, Colour, Amount), shared across the whole shop. Which of them a product uses is chosen on that product, under Variants.',
        empty: "No option types yet. Add one below, or add it from a product's Variants section.",
        deleteType: 'Delete {name} from this shop',
        deleteValue: 'Delete "{value}" from this shop',
        noValues: 'No values yet: a product using this has nothing to offer.',
        newTypePlaceholder: 'New option type (e.g. Size)',
        addType: 'Add type',
        confirmDeleteType: 'Delete "{name}" and all its values from this shop?',
        addTypeFailed: 'Could not add that option type.',
        deleteTypeFailed: 'Could not delete that option type.',
        addValueFailed: 'Could not add that value.',
        deleteValueFailed: 'Could not delete that value.',
    },
    stockInput: {
        invalid: 'Stock must be a whole number, 0 or more.',
        save: 'Save stock',
        edit: 'Edit {label}',
    },
    tagInput: {
        placeholder: 'Type a tag, press Enter…',
    },
    picker: {
        label: 'Product',
        placeholder: 'Search products…',
        loadFailed: 'Failed to load products',
        empty: 'No products match.',
        draft: '(draft)',
    },
};
